import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, Chip } from "@mui/material";
import { X, Sparkles, Wrench, Bug } from "lucide-react";
import { useNavigate } from "react-router-dom";
import pkg from "../../package.json";
import { Button } from "@/components/Button/Button";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/sidebar";
import { CHANGELOG, ChangelogEntry } from "@/config/changelog";

const typeConfig = {
  new: { label: "New", color: "#1e40af", bg: "#dbeafe", Icon: Sparkles },
  improved: { label: "Improved", color: "#065f46", bg: "#d1fae5", Icon: Wrench },
  fixed: { label: "Fixed", color: "#92400e", bg: "#fef3c7", Icon: Bug },
} as const;

const ChangelogDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
    <DialogTitle sx={{ pb: 1 }}>
      <div className="flex items-center justify-between">
        <span className="text-base font-semibold">Changelog</span>
        <Button variant="icon" size="sm" onClick={onClose} type="button">
          <X className="size-4" />
        </Button>
      </div>
    </DialogTitle>
    <DialogContent dividers sx={{ pt: 2 }}>
      <div className="flex flex-col gap-6">
        {CHANGELOG.map((entry: ChangelogEntry) => (
          <div key={entry.version}>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-sm font-bold">v{entry.version}</span>
              <span className="text-xs text-muted-foreground">
                {new Date(entry.date).toLocaleDateString("nb-NO", { year: "numeric", month: "long", day: "numeric" })}
              </span>
            </div>
            <ul className="flex flex-col gap-1.5">
              {entry.changes.map((change, i) => {
                const { label, color, bg, Icon } = typeConfig[change.type];
                return (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Chip
                      label={label}
                      size="small"
                      icon={<Icon style={{ width: 10, height: 10 }} />}
                      sx={{
                        fontSize: "0.55rem",
                        height: "18px",
                        bgcolor: bg,
                        color,
                        flexShrink: 0,
                        mt: "1px",
                        "& .MuiChip-icon": { marginLeft: "6px", marginRight: "-2px" },
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                    <span className="text-xs text-foreground leading-snug">{change.description}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export function TeamSwitcher() {
  const navigate = useNavigate();
  const [changelogOpen, setChangelogOpen] = useState(false);

  return (
    <>
      <ChangelogDialog open={changelogOpen} onClose={() => setChangelogOpen(false)} />
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            onClick={() => navigate("/")}
            size="lg"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            {/* Logo: 4 circles forming a square */}
            <div className="flex gap-0 w-8 h-8 flex-shrink-0">
              <div className="flex flex-col gap-0">
                <div className="w-4 h-4 rounded-full bg-primary" />
                <div className="w-4 h-4 rounded-full bg-primary" />
              </div>
              <div className="flex flex-col gap-0">
                <div className="w-4 h-4 rounded-full bg-primary" />
                <div className="w-4 h-4 rounded-full bg-primary" />
              </div>
            </div>

            {/* Text */}
            <div className="flex flex-col flex-1 justify-center text-left gap-0">
              <span className="text-lg leading-none">
                THE<span className="font-bold">REAL</span>
              </span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-base font-light tracking-widest leading-none">
                  ADVISOR
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setChangelogOpen(true);
                  }}
                  className="text-[9px] font-semibold tracking-wide leading-none px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors cursor-pointer dark:bg-emerald-900/30 dark:text-emerald-400"
                >
                  v{pkg.version}
                </span>
              </div>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
}
